// ============================================
// Choice UI Controller
// Renders choice buttons with cost badges,
// stat previews, disabled states, keyboard nav
// GSAP-powered entrance animations
// ============================================

import { events, Events } from '../engine/events.js';
import { L } from '../systems/localization.js';
import { storyContent, getImageManifest } from './story-content.js';
import { getImageUrl } from '../image-loader.js';
import { appearanceState } from '../systems/appearance.js';
import { getEffectiveFemGain, getEffectiveSubGain, getEffectiveShameGain } from '../engine/externals.js';

/* global gsap */

class ChoicesUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._container = null;
        /** @type {HTMLElement|null} */
        this._panel = null;
        /** @type {Function|null} Callback when a choice is selected */
        this.onChoiceSelected = null;
        /** @type {boolean} */
        this._bound = false;
        /** @type {boolean} Debounce flag to prevent double-clicks */
        this._choicePending = false;
    }

    // ------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------

    init() {
        if (this._bound) return;
        this._bound = true;

        this._scope = events.createScope('choices');

        this._fixedContainer = document.getElementById('choices');
        this._panel = document.getElementById('choices-panel');
        this._storyContent = document.getElementById('story-content');

        // On desktop (>=768px): render choices inline in story-content
        // On mobile (<768px): render in fixed bottom panel
        this._updateContainer();
        // Stored for destroy() — bare anonymous listeners leaked across
        // re-inits (tests) and double-bound keyboard navigation.
        this._onResize = () => this._updateContainer();
        window.addEventListener('resize', this._onResize);
        // Android WebView visualViewport sibling (BUG_PATTERNS § 76).
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this._onResize);
        }

        this._bindKeyboard();

        // Listen for choices from engine
        this._scope.on(Events.STORY_CHOICES_READY, (choices) => this._renderChoices(choices));
        this._scope.on(Events.STORY_CLEAR, () => this._clear());
        this._scope.on(Events.STORY_APPEND, () => this._clear());
        this._scope.on(Events.STORY_END, () => this._showEndState());

        // When typing completes, enable pending choices
        this._scope.on(Events.STORY_TYPING_COMPLETE, () => this._enablePendingChoices());
    }

    destroy() {
        this._teardownContinueMode();
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
        if (this._onResize) {
            window.removeEventListener('resize', this._onResize);
            if (window.visualViewport) window.visualViewport.removeEventListener('resize', this._onResize);
            this._onResize = null;
        }
        if (this._keydownHandler) {
            document.removeEventListener('keydown', this._keydownHandler);
            this._keydownHandler = null;
        }
        this._bound = false;
    }

    // ------------------------------------------------------------------
    // Public API
    // ------------------------------------------------------------------

    /**
     * Set the callback for when a choice is selected.
     * @param {Function} callback - (choiceIndex: number) => void
     */
    setChoiceHandler(callback) {
        this.onChoiceSelected = callback;
    }

    /**
     * Clear all choices.
     */
    clear() {
        this._clear();
    }

    // ------------------------------------------------------------------
    // Private: Rendering
    // ------------------------------------------------------------------

    /**
     * Render choices into the grid.
     * @param {Array<object>} choices - flat choice objects from StoryManager
     *   Properties: index, text, isEnabled, energyCost, timeCost, femGain, subGain,
     *   shameGain, arousalGain, isDramatic, disabledReason, color, wouldExhaust, etc.
     */
    _renderChoices(choices) {
        this._updateContainer();
        if (!this._container) return;
        this._container.innerHTML = '';
        this._teardownContinueMode();

        if (!choices || choices.length === 0) return;

        // ── "Click to continue" mode ──
        // Single enabled choice with no stats/cost = just a Continue button → replace with tap-to-continue
        if (this._isContinueChoice(choices)) {
            this._setupContinueMode(choices[0]);
            return;
        }

        // Determine if these are navigation choices (no energy/time costs, no images)
        const hasAnyCost = choices.some(c => c.energyCost >= 0 || c.timeCost > 0 || c.choiceImg);

        const btns = [];
        choices.forEach((choice, i) => {
            const btn = this._createChoiceButton(choice, i, !hasAnyCost);
            this._container.appendChild(btn);
            btns.push(btn);
        });

        // Add invisible spacer for odd count (keeps 2-col grid even)
        if (choices.length % 2 === 1) {
            const spacer = document.createElement('div');
            spacer.className = 'choice-btn';
            spacer.style.visibility = 'hidden';
            spacer.style.pointerEvents = 'none';
            spacer.setAttribute('aria-hidden', 'true');
            this._container.appendChild(spacer);
        }

        // Inline: append to story-content and scroll into view (always, both desktop + mobile)
        if (this._container === this._inlineContainer && this._storyContent) {
            // End-of-scene fleuron: quiet ornament between the last narrative
            // line and the choice buttons, so players know the scroll is
            // complete and aren't looking for more text. Skipped for
            // location-hub navigation (pure action menus) — only meaningful
            // at actual narrative endings.
            if (hasAnyCost) {
                const existing = this._storyContent.querySelector(':scope > .scene-end-fleuron:last-of-type');
                // Always drop a fresh one — a scene may re-render choices after
                // a disabled-option click, and previous fleurons were already
                // cleared with _storyContent on scene change.
                if (!existing || existing.previousElementSibling) {
                    const fleur = document.createElement('div');
                    fleur.className = 'scene-end-fleuron';
                    fleur.setAttribute('aria-hidden', 'true');
                    this._storyContent.appendChild(fleur);
                }
            }
            this._storyContent.appendChild(this._inlineContainer);
            setTimeout(() => {
                this._scrollContainerToReveal(this._inlineContainer);
            }, 100);
        }

        // If text is still typing, show choices in pending (disabled) state
        if (storyContent.isTyping && this._container) {
            this._container.classList.add('choices-pending');
            // Safety: auto-enable after 3s in case STORY_TYPING_COMPLETE never fires
            setTimeout(() => this._enablePendingChoices(), 3000);
        }

        // GSAP staggered entrance
        if (typeof gsap !== 'undefined' && btns.length > 0) {
            gsap.fromTo(btns,
                { opacity: 0, y: 12, scale: 0.96 },
                { opacity: 1, y: 0, scale: 1, duration: 0.35, ease: 'back.out(1.4)', stagger: 0.06 }
            );
        }
    }

    /**
     * Create a single choice button element.
     * @param {object} choice
     * @param {number} displayIndex - 0-based position
     * @param {boolean} isNavigation - whether this is a nav-only choice set
     * @returns {HTMLButtonElement}
     */
    _createChoiceButton(choice, displayIndex, isNavigation) {
        const btn = document.createElement('button');
        const num = displayIndex + 1;

        // Base classes
        let classes = 'choice-btn';
        if (isNavigation) classes += ' nav-choice';
        if (choice.isDramatic) classes += ' dramatic';
        if (!choice.isEnabled) classes += ' disabled';
        if (choice.wouldAutoSleep) classes += ' choice-auto-sleep';
        if (choice.wouldExhaust) classes += ' choice-exhaust';

        btn.className = classes;
        if (choice.originalText) btn.dataset.original = choice.originalText.toLowerCase();
        // v0.2.6: apply #color: tag value as data-color attribute so CSS
        // can style per-color. Currently consumed: 'safeword' (amber border
        // + ⚠ badge for Kennel scenes). Pink/resist remain latent — the
        // tag has been used in Ink for ages but was never rendered.
        if (choice.color) btn.dataset.color = choice.color;
        // Audit D21: Choice-card tier-variants. Stat-gain magnitude drives a
        // left-border accent so the player can tell a +1 Fem from a +3 Fem
        // beat without reading the preview pill. dramatic > high-sub > high-fem
        // (priority: dramatic is rare and load-bearing, sub gain is the more
        // narratively-loaded shift, fem is the most common). Effective values
        // are used so pacing/diminishing returns are honoured.
        // Pacing 2.0: read the RAW tag (femTag/subTag) for the "high-fem/sub" accent
        // so it reflects the #fem:/#sub: magnitude, not the accum-shrunk effective tick.
        const femEff = (choice.femTag !== undefined && choice.femTag !== -1 ? choice.femTag : (choice.femGain || 0));
        const subEff = (choice.subTag !== undefined && choice.subTag !== -1 ? choice.subTag : (choice.subGain || 0));
        if (choice.isDramatic) btn.dataset.tier = 'dramatic';
        else if (subEff >= 2) btn.dataset.tier = 'high-sub';
        else if (femEff >= 2) btn.dataset.tier = 'high-fem';

        // Disabled state — UX-03: aria-disabled instead of native `disabled`.
        // Native disabled buttons are inert in the keyboard tab order, so
        // keyboard-only users were silently skipping them and never seeing
        // the gating reason. aria-disabled keeps focusability while CSS
        // (`.choice-btn[aria-disabled="true"] { pointer-events: none }`)
        // blocks pointer interaction. Click handler (line 327) is only
        // attached for enabled choices, so Enter on aria-disabled is safe.
        if (!choice.isEnabled) {
            btn.setAttribute('aria-disabled', 'true');
            if (choice.disabledReason) {
                btn.title = choice.disabledReason;
                // Screen reader: prepend reason to label so the lock state is announced.
                const labelText = (choice.text || '').replace(/<[^>]+>/g, '').trim();
                btn.setAttribute('aria-label', `${labelText} — locked: ${choice.disabledReason}`);
            }
        }

        // Thumbnail image (if #choiceimg: tag set)
        if (choice.choiceImg) {
            const imgPath = this._resolveChoiceImage(choice.choiceImg);
            if (imgPath) {
                const thumbWrap = document.createElement('div');
                thumbWrap.className = 'choice-thumb';
                const thumbImg = document.createElement('img');
                thumbImg.alt = '';
                thumbImg.decoding = 'async';
                thumbImg.src = imgPath;
                // Fallback: try .png if .webp fails
                thumbImg.onerror = () => {
                    if (thumbImg.src.endsWith('.webp')) {
                        thumbImg.src = thumbImg.src.replace('.webp', '.png');
                    } else {
                        thumbWrap.style.display = 'none';
                        btn.classList.remove('has-thumb');
                    }
                };
                thumbWrap.appendChild(thumbImg);
                btn.appendChild(thumbWrap);
                btn.classList.add('has-thumb');
            }
        }

        // Choice content
        const textEl = document.createElement('span');
        textEl.className = 'choice-text';

        const numSpan = document.createElement('span');
        numSpan.className = 'choice-number';
        numSpan.textContent = num;

        textEl.appendChild(numSpan);
        textEl.appendChild(document.createTextNode(` ${choice.text}`));

        // For thumbnail choices: wrap text + disabled reason + badges in a column container.
        // Badges go BELOW text (not inline inside .choice-text) — .choice-text has
        // overflow:hidden, so when long text wraps to multiple lines on mobile, an
        // inline-appended badge container would scroll off the clipped region (the
        // c4 Laser Hair Removal "missing cost/stats" bug, foundby:Pea v0.2.6.2 Free).
        const hasThumbnail = btn.classList.contains('has-thumb');
        if (hasThumbnail) {
            const contentWrap = document.createElement('div');
            contentWrap.className = 'choice-content-wrap';
            contentWrap.appendChild(textEl);
            if (!choice.isEnabled && choice.disabledReason) {
                const reason = document.createElement('div');
                reason.className = 'choice-disabled-reason';
                reason.textContent = choice.disabledReason;
                contentWrap.appendChild(reason);
            }
            if (!isNavigation && choice.isEnabled) {
                const badges = this._buildBadges(choice);
                if (badges) {
                    badges.classList.add('choice-badges-inline');
                    contentWrap.appendChild(badges);
                }
            }
            btn.appendChild(contentWrap);
        } else {
            btn.appendChild(textEl);
            if (!choice.isEnabled && choice.disabledReason) {
                const reason = document.createElement('div');
                reason.className = 'choice-disabled-reason';
                reason.textContent = choice.disabledReason;
                btn.appendChild(reason);
            }
        }

        // Cost badges (standard layout — below text, only for non-thumbnail choices)
        if (!hasThumbnail && !isNavigation && choice.isEnabled) {
            const badges = this._buildBadges(choice);
            if (badges) btn.appendChild(badges);
        }

        // Hover tooltip: show stat summary for choices with costs
        if (choice.isEnabled && !isNavigation && (choice.energyCost >= 0 || choice.femGain >= 0 || choice.subGain >= 0)) {
            let tip = null;
            btn.addEventListener('mouseenter', () => {
                const parts = [];
                const _tE = L.get('stat.energy'), _tT = L.get('choice.time'), _tF = L.get('stat.fem_short'), _tS = L.get('badge.sub'), _tSh = L.get('badge.shame'), _tA = L.get('stat.arousal');
                const eLabel = _tE !== 'stat.energy' ? _tE : 'Energy';
                if (choice.energyCost >= 0) {
                    // Use effective cost when modifiers shifted it (Arousal penalty, HRT
                    // irritable, Comfort, Drunk numbness). Otherwise the player sees
                    // -5 promised on the button and pays -12 from the hidden multipliers.
                    const eff = choice.effectiveEnergyCost;
                    const showEff = (typeof eff === 'number' && eff !== choice.energyCost);
                    parts.push(`${eLabel}: -${showEff ? eff : choice.energyCost}`);
                }
                if (choice.timeCost > 0) parts.push(`${_tT !== 'choice.time' ? _tT : 'Time'}: +${choice.timeCost}`);
                if (choice.femGain > 0) parts.push(`${_tF !== 'stat.fem_short' ? _tF : 'Fem'}: +${choice.femGain}`);
                else if (choice.femGain < 0 && choice.femGain !== -1) parts.push(`${_tF !== 'stat.fem_short' ? _tF : 'Fem'}: ${choice.femGain}`);
                if (choice.subGain > 0) parts.push(`${_tS !== 'badge.sub' ? _tS : 'Sub'}: +${choice.subGain}`);
                else if (choice.subGain < 0 && choice.subGain !== -1) parts.push(`${_tS !== 'badge.sub' ? _tS : 'Sub'}: ${choice.subGain}`);
                if (choice.shameGain > 0) parts.push(`${_tSh !== 'badge.shame' ? _tSh : 'Shame'}: +${choice.shameGain}`);
                else if (choice.shameGain < 0 && choice.shameGain !== -1) parts.push(`${_tSh !== 'badge.shame' ? _tSh : 'Shame'}: ${choice.shameGain}`);
                if (choice.arousalGain > 0) parts.push(`${_tA !== 'stat.arousal' ? _tA : 'Arousal'}: +${choice.arousalGain}`);
                else if (choice.arousalGain < 0 && choice.arousalGain !== -1) parts.push(`${_tA !== 'stat.arousal' ? _tA : 'Arousal'}: ${choice.arousalGain}`);
                if (parts.length === 0) return;

                // Modifier-Breakdown line — appears below the cost summary when the
                // cost was shifted from base. Mirrors the badge-title format so the
                // player gets the same breakdown whether they hover the badge icon
                // or the button body.
                const breakdown = [];
                if (choice.effectiveEnergyCost !== undefined && choice.effectiveEnergyCost !== choice.energyCost) {
                    breakdown.push(`${L.get('badge.energy_base') || 'Base'}: ${choice.energyCost}`);
                    if (choice.comfortDiscount > 0) breakdown.push(`${L.get('stat.comfort') || 'Comfort'}: −${choice.comfortDiscount}`);
                if (choice.comfortPenalty > 0) breakdown.push(`${L.get('stat.comfort') || 'Comfort'}: +${choice.comfortPenalty}`);
                    if (choice.moodExtra > 0) breakdown.push(`${L.get('badge.energy_mood') || 'HRT irritable'}: +${choice.moodExtra}`);
                    if (choice.arousalPenalty > 0) breakdown.push(`${_tA !== 'stat.arousal' ? _tA : 'Arousal'}: +${choice.arousalPenalty}`);
                    if (choice.drunkDiscount > 0) breakdown.push(`${L.get('badge.energy_drunk') || 'Drunk'}: −${choice.drunkDiscount}`);
                }

                tip = document.createElement('div');
                tip.className = 'choice-hover-tip';
                if (breakdown.length) {
                    tip.classList.add('has-breakdown');
                    tip.innerHTML = `<div>${parts.join('  ·  ')}</div><div class="choice-hover-breakdown">${breakdown.join('  ·  ')}</div>`;
                } else {
                    tip.textContent = parts.join('  ·  ');
                }
                document.body.appendChild(tip);
                const rect = btn.getBoundingClientRect();
                tip.style.left = `${rect.left + rect.width / 2}px`;
                tip.style.top = `${rect.top - 6}px`;
            });
            btn.addEventListener('mouseleave', () => { if (tip) { tip.remove(); tip = null; } });
            // Also remove on click (button gets destroyed before mouseleave fires)
            btn.addEventListener('click', () => { if (tip) { tip.remove(); tip = null; } });
        }

        // Click handler (with double-click debounce + GSAP feedback)
        if (choice.isEnabled) {
            btn.addEventListener('click', () => {
                if (this._choicePending) return;
                if (this.onChoiceSelected) {
                    this._choicePending = true;
                    if (this._container) {
                        this._container.classList.add('choices-disabled');
                    }
                    // GSAP click feedback: pulse + fade selected, dim others
                    if (typeof gsap !== 'undefined') {
                        const siblings = this._container ? [...this._container.querySelectorAll('.choice-btn:not([aria-hidden])')] : [];
                        const others = siblings.filter(b => b !== btn);
                        gsap.to(btn, { scale: 0.95, duration: 0.1, yoyo: true, repeat: 1, ease: 'power2.inOut' });
                        if (others.length > 0) {
                            gsap.to(others, { opacity: 0.3, duration: 0.2, ease: 'power2.out' });
                        }
                    }
                    events.emit(Events.AUDIO_SFX_PLAY, 'mouseclick1');
                    try {
                        this.onChoiceSelected(choice.index);
                    } finally {
                        // Always schedule the debounce reset, even if the
                        // handler threw — otherwise _choicePending sticks true
                        // and every subsequent choice click is silently ignored.
                        setTimeout(() => {
                            this._choicePending = false;
                            if (this._container) {
                                this._container.classList.remove('choices-disabled');
                            }
                        }, 300);
                    }
                }
            });
        }

        // Initial state for GSAP entrance (opacity set by gsap.fromTo)
        btn.style.opacity = '0';

        return btn;
    }

    /**
     * Build the badges container for a choice.
     *
     * Stat-gain badges (arousal / fem / sub / shame) render with an icon
     * followed by the delta. Energy + time keep text labels since they're
     * gameplay costs, not stat stores. Icons reuse the same PNGs as the
     * STATS panel — the `A` / `Fem` / `Sub` / `Shame` abbreviations that
     * used to read as ambiguous (the player report on "+8 A") are gone.
     *
     * @param {object} tags - parsed choice tags
     * @returns {HTMLElement|null}
     */
    _buildBadges(tags) {
        const badges = [];

        // Energy cost — icon + number, same pattern as the stat-gain
        // badges. Zero is shown as a dash so players notice "free" actions.
        // When hidden multipliers (Arousal penalty, HRT-irritable, Comfort,
        // Drunk numbness) shift the cost away from the base #energy:N tag,
        // show the EFFECTIVE cost on the badge and put the breakdown in the
        // tooltip — otherwise the player sees -30 promised and pays -38,
        // which reads as a bug ("energy tax" support tickets).
        if (tags.energyCost >= 0) {
            const eff = tags.effectiveEnergyCost;
            const showEff = (typeof eff === 'number' && eff !== tags.energyCost);
            const txt = showEff
                ? String(eff)
                : (tags.energyCost === 0 ? '0' : String(tags.energyCost));
            const energyLabel = L.get('stat.energy') || 'Energy';

            let title = `${txt} ${energyLabel}`;
            if (showEff) {
                const lines = [`${energyLabel}: ${eff}`,
                               `${L.get('badge.energy_base') || 'Base'}: ${tags.energyCost}`];
                if (tags.comfortDiscount > 0) {
                    lines.push(`${L.get('stat.comfort') || 'Comfort'}: −${tags.comfortDiscount}`);
                }
                if (tags.comfortPenalty > 0) {
                    lines.push(`${L.get('stat.comfort') || 'Comfort'}: +${tags.comfortPenalty}`);
                }
                if (tags.moodExtra > 0) {
                    lines.push(`${L.get('badge.energy_mood') || 'HRT irritable'}: +${tags.moodExtra}`);
                }
                if (tags.arousalPenalty > 0) {
                    lines.push(`${L.get('stat.arousal') || 'Arousal'}: +${tags.arousalPenalty}`);
                }
                if (tags.drunkDiscount > 0) {
                    lines.push(`${L.get('badge.energy_drunk') || 'Drunk'}: −${tags.drunkDiscount}`);
                }
                title = lines.join('\n');
            }

            badges.push(this._iconBadgeImg(
                'badge-energy' + (showEff ? ' badge-energy-modified' : ''),
                'assets/images/UI/icon_stat_energy.png',
                txt,
                energyLabel,
                title
            ));
        }

        // Time cost — icon (hourglass/wait) + number, only when > 0.
        if (tags.timeCost > 0) {
            badges.push(this._iconBadgeImg(
                'badge-time',
                'assets/images/UI/icon_topbar_wait.png',
                String(tags.timeCost),
                L.get('badge.time') || 'Time'
            ));
        }

        // Stat-preview badges use icons instead of text abbreviations
        if (tags.arousalGain > 0) {
            badges.push(this._iconBadge('badge-arousal', 'arousal', `+${tags.arousalGain}`, L.get('stat.arousal') || 'Arousal'));
        } else if (tags.arousalGain < 0 && tags.arousalGain !== -1) {
            badges.push(this._iconBadge('badge-arousal', 'arousal', `${tags.arousalGain}`, L.get('stat.arousal') || 'Arousal'));
        }
        // v0.2.7 audit H5: choice-tag previews are pacing-aware. Base values
        // from #fem:/#sub:/#shame: tags are pre-wrapper; the wrappers
        // (fem_gain/sub_gain/shame_gain) apply pacing phase-lock + DR + trait
        // modifiers. Showing the raw tag for a Day-1 W1-locked PC promises
        // +3 Fem when the wrapper delivers 0 — trust killer in both directions.
        // The renderer now displays the effective number that the wrappers
        // will actually grant, with the raw value in the tooltip for context.
        const story = window._sm?.currentStory;
        // N5 fix (2026-05-26): for negative gains, compute floor-clamped
        // effective loss. fem_loss/sub_loss/shame_gain(negative) helpers
        // in Globals.ink clamp at 0. Without this, a "-2 Fem" badge at Fem 0
        // promised a stat change that did nothing (foundby:playthrough 2 N5).
        const effLoss = (base, stat) => {
            if (base >= 0) return base;
            const cur = story?.variablesState?.[stat] || 0;
            return Math.max(-cur, base);  // -2 vs -Fem(0) → 0
        };
        // Pacing 2.0: badges compute from the RAW #fem:/#sub:/#shame: tag
        // (femTag/subTag/shameTag), NOT the story.js-processed femGain/subGain,
        // which is already the EFFECTIVE value for #energy choices (story.js:1728).
        // Re-applying the wrapper double-DR'd it, harmless before the fractional
        // accumulator (the old +1 floor kept it >= 1), but now the second pass can
        // floor a real +1 tick down to 0 and hide the badge entirely. Fall back to
        // femGain where no raw tag exists. Reviewer-confirmed 2026-07-01.
        const rawFem = tags.femTag !== undefined ? tags.femTag : tags.femGain;
        const rawSub = tags.subTag !== undefined ? tags.subTag : tags.subGain;
        const rawShame = tags.shameTag !== undefined ? tags.shameTag : tags.shameGain;
        if (rawFem !== -1 && rawFem !== 0) {
            const base = rawFem;
            const eff = base > 0 ? getEffectiveFemGain(base, story) : effLoss(base, 'Femininity');
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                const tooltipBase = base !== eff ? ` (base ${base > 0 ? '+' : ''}${base})` : '';
                badges.push(this._iconBadge('badge-fem', 'fem', `${sign}${eff}`,
                    (L.get('stat.femininity') || 'Femininity') + tooltipBase));
            }
        }
        if (rawSub !== -1 && rawSub !== 0) {
            const base = rawSub;
            const eff = base > 0 ? getEffectiveSubGain(base, story) : effLoss(base, 'Submissiveness');
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                const tooltipBase = base !== eff ? ` (base ${base > 0 ? '+' : ''}${base})` : '';
                badges.push(this._iconBadge('badge-sub', 'sub', `${sign}${eff}`,
                    (L.get('stat.submissiveness') || 'Submissiveness') + tooltipBase));
            }
        }
        if (rawShame !== -1 && rawShame !== 0) {
            const base = rawShame;
            const eff = base > 0 ? getEffectiveShameGain(base, story) : effLoss(base, 'Shame');
            if (eff !== 0) {
                const sign = eff > 0 ? '+' : '';
                const tooltipBase = base !== eff ? ` (base ${base > 0 ? '+' : ''}${base})` : '';
                badges.push(this._iconBadge('badge-shame', 'shame', `${sign}${eff}`,
                    (L.get('stat.shame') || 'Shame') + tooltipBase));
            }
        }

        // Warnings
        if (tags.wouldExhaust) {
            const wLabel = L.get('badge.low_energy');
            badges.push(this._badge('badge-warn', `\u26A0 ${wLabel !== 'badge.low_energy' ? wLabel : 'Low Energy'}`));
        }
        if (tags.wouldAutoSleep) {
            const asLabel = L.get('badge.auto_sleep');
            badges.push(this._badge('badge-warn', `\uD83C\uDF19 ${asLabel !== 'badge.auto_sleep' ? asLabel : 'Auto-Sleep'}`));
        }

        if (badges.length === 0) return null;

        const container = document.createElement('div');
        container.className = 'choice-badges';
        badges.forEach(b => container.appendChild(b));
        return container;
    }

    /**
     * Create a single badge element.
     * @param {string} cssClass
     * @param {string} text
     * @returns {HTMLSpanElement}
     */
    _badge(cssClass, text) {
        const span = document.createElement('span');
        span.className = `choice-badge ${cssClass}`;
        span.textContent = text;
        return span;
    }

    /**
     * Build a badge with a stat icon + delta text.
     * @param {string} cssClass - e.g. 'badge-fem'
     * @param {string} iconKey - base name: 'fem', 'sub', 'shame', 'arousal'
     * @param {string} deltaText - e.g. '+8', '-2'
     * @param {string} a11yLabel - full stat name for screen readers / tooltips
     */
    _iconBadge(cssClass, iconKey, deltaText, a11yLabel) {
        return this._iconBadgeImg(
            cssClass,
            `assets/images/UI/icon_stat_${iconKey}.png`,
            deltaText,
            a11yLabel
        );
    }

    /**
     * Generalised icon badge — takes an arbitrary image path. Used by
     * Energy + Time which pull from different UI icons than the stat
     * panel's `icon_stat_*.png` set.
     */
    _iconBadgeImg(cssClass, iconSrc, deltaText, a11yLabel, customTitle) {
        const span = document.createElement('span');
        span.className = `choice-badge ${cssClass} badge-with-icon`;
        span.title = customTitle || `${deltaText} ${a11yLabel}`;
        const img = document.createElement('img');
        img.className = 'badge-icon';
        img.src = iconSrc;
        img.alt = a11yLabel;
        img.setAttribute('aria-hidden', 'true');
        span.appendChild(img);
        span.appendChild(document.createTextNode(deltaText));
        return span;
    }

    // ------------------------------------------------------------------
    // "Click to continue" mode — replaces single Continue buttons
    // ------------------------------------------------------------------

    /** Patterns that count as a "continue" choice (case-insensitive) */
    static CONTINUE_PATTERNS = /^(continue|\.{3}|…|next|weiter|suivant|continuer|devam|continuar|继续|下一步)$/i;

    /** Check if this choice set is a single "Continue" button with no gameplay */
    _isContinueChoice(choices) {
        if (choices.length !== 1) return false;
        const c = choices[0];
        if (!c.isEnabled) return false;
        // Must have no stat costs/gains
        if (c.energyCost >= 0 || c.timeCost > 0) return false;
        if (c.femGain > 0 || c.subGain > 0 || c.shameGain > 0 || c.arousalGain > 0) return false;
        if (c.choiceImg || c.isDramatic || c.color) return false;
        // Text must match Continue pattern
        const text = (c.text || '').trim();
        return ChoicesUI.CONTINUE_PATTERNS.test(text);
    }

    /** Replace choice button with a visible Continue button + click-anywhere */
    _setupContinueMode(choice) {
        this._continueChoiceIndex = choice.index;

        // Show a visible Continue button (always clickable, never hidden)
        const indicator = document.createElement('div');
        indicator.className = 'continue-indicator';
        // UX-02: include Space/Enter keyboard hint inline so non-mouse users discover the shortcut.
        // Hidden on mobile via CSS (touch users don't have a keyboard).
        const continueLabel = (typeof L !== 'undefined' && L.get) ? (L.get('ui.continue') || 'Continue') : 'Continue';
        indicator.innerHTML = '<button class="choice-btn continue-btn">'
            + continueLabel
            + ' <span class="continue-pulse">&#9660;</span>'
            + ' <span class="continue-kbd-hint">[Space]</span>'
            + '</button>';
        indicator.setAttribute('aria-label', 'Click to continue, or press Space');
        // F27: extend the 600ms setup debounce to the explicit Continue
        // button too. Pre-fix only the click-anywhere handler debounced —
        // rapid-clicks on the visible button bypassed it and double-fired
        // _fireContinue. _choicePending offers some protection but its
        // 300ms window doesn't cover the 600ms ramp-up.
        const continueSetupTime = Date.now();
        indicator.querySelector('.continue-btn').addEventListener('click', (e) => {
            e.stopPropagation();
            if (Date.now() - continueSetupTime < 600) return;
            this._fireContinue();
        });

        // Always append inline to story-content (scroll-append mode)
        if (this._storyContent) {
            this._storyContent.appendChild(indicator);
        } else if (this._container) {
            this._container.appendChild(indicator);
        }
        setTimeout(() => this._scrollContainerToReveal(indicator), 150);

        this._continueIndicator = indicator;

        // Click-anywhere-to-continue: only on touch devices. On desktop (mouse +
        // hover) the click-anywhere handler caused players to miss content when
        // they meant to click into the story area to scroll back — Pablito
        // 2026-05-05 v0.2.5.7.4. Desktop users have the visible Continue button
        // and Space/Enter; mobile users keep tap-anywhere because the button is
        // small and "tap to continue" is the genre standard.
        const hasHover = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(hover: hover)').matches;
        const setupTime = Date.now();
        if (!hasHover) {
            this._continueHandler = (e) => {
                if (Date.now() - setupTime < 600) return;
                if (e.target.closest('button:not(.choice-btn):not(.continue-btn)') || e.target.closest('a') || e.target.closest('.overlay') || e.target.closest('img')) return;
                this._fireContinue();
            };
            const storyScroll = document.getElementById('story-scroll') || document.getElementById('center-panel');
            if (storyScroll) storyScroll.addEventListener('click', this._continueHandler);
        }

        // Space/Enter to continue. Same guards as the number/arrow handler in
        // _bindKeyboard: typing Space/Enter into a form field (bug-report /
        // feedback overlay textarea) or over an open overlay must never fire
        // the background Continue — the story advanced invisibly while a
        // player typed a report (VPS 0.3.0.3, 2026-07-14).
        this._continueKeyHandler = (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
                const t = e.target;
                if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
                    || t.tagName === 'SELECT' || t.isContentEditable)) return;
                if (this._anyOverlayOpen()) return;
                e.preventDefault();
                this._fireContinue();
            }
        };
        document.addEventListener('keydown', this._continueKeyHandler);
    }

    _fireContinue() {
        if (this._continueChoiceIndex == null) return;
        const idx = this._continueChoiceIndex;
        this._teardownContinueMode();
        events.emit(Events.AUDIO_SFX_PLAY, 'mouseclick1');
        if (this.onChoiceSelected) this.onChoiceSelected(idx);
    }

    _teardownContinueMode() {
        if (this._continueIndicator) {
            this._continueIndicator.remove();
            this._continueIndicator = null;
        }
        if (this._continueHandler) {
            const storyScroll = document.getElementById('story-scroll') || document.getElementById('center-panel');
            if (storyScroll) storyScroll.removeEventListener('click', this._continueHandler);
            this._continueHandler = null;
        }
        if (this._continueKeyHandler) {
            document.removeEventListener('keydown', this._continueKeyHandler);
            this._continueKeyHandler = null;
        }
        this._continueChoiceIndex = null;
    }

    /**
     * Always use inline container inside story-content (scroll-append mode).
     * Mobile and desktop both render choices inline in the scroll flow.
     */
    _updateContainer() {
        if (this._storyContent) {
            // Always inline: create/reuse inline container inside story-content
            if (!this._inlineContainer) {
                this._inlineContainer = document.createElement('div');
                this._inlineContainer.id = 'choices-inline';
                this._inlineContainer.className = 'choices-grid';
            }
            this._container = this._inlineContainer;
        } else {
            // Fallback only if story-content not available
            this._container = this._fixedContainer;
        }
    }

    /**
     * Resolve a choice thumbnail image path.
     * Uses tar blob URL, manifest lookup, or direct path.
     * @param {string} name - image name (e.g. 'master_reward_collar')
     * @returns {string|null}
     */
    _resolveChoiceImage(name) {
        // Resolve the appearance variant (masc / chastity / longhair / boobs) so
        // the thumbnail matches the player's CURRENT presentation, the same axis
        // resolution the scene #img: uses (story-content.js). Without this a
        // masc-presenting / early male PC saw the feminine base thumbnail (e.g.
        // beach_swim → pink bikini instead of beach_swim_masc → trunks).
        // resolveVariantName only swaps when the variant file actually exists,
        // so choiceimgs without a variant fall through to the base untouched.
        let resolved = name;
        try { resolved = appearanceState.resolveVariantName(name) || name; } catch (e) { resolved = name; }

        // Tar mode: blob URL (try variant first, then base)
        const blobUrl = getImageUrl(resolved) || (resolved !== name ? getImageUrl(name) : null);
        if (blobUrl) return blobUrl;

        // Manifest lookup: name → folder (try variant first, then base)
        const manifest = getImageManifest();
        if (manifest) {
            if (manifest[resolved]) return `assets/images/${manifest[resolved]}/${resolved}.webp`;
            if (manifest[name]) return `assets/images/${manifest[name]}/${name}.webp`;
        }

        // No manifest entry — can't resolve
        return null;
    }

    // ------------------------------------------------------------------
    // Private: State
    // ------------------------------------------------------------------

    _clear() {
        // Remove any orphaned hover tooltips
        document.querySelectorAll('.choice-hover-tip').forEach(t => t.remove());
        // Tear down continue mode handlers
        this._teardownContinueMode();
        // Clear both containers
        if (this._fixedContainer) {
            this._fixedContainer.innerHTML = '';
            this._fixedContainer.classList.remove('choices-disabled', 'choices-pending');
        }
        if (this._inlineContainer) {
            this._inlineContainer.innerHTML = '';
            this._inlineContainer.classList.remove('choices-disabled', 'choices-pending');
            this._inlineContainer.remove();
        }
        this._choicePending = false;
    }

    /**
     * Remove the pending (disabled) state from choices once text typing finishes.
     */
    _enablePendingChoices() {
        if (!this._container || !this._container.classList.contains('choices-pending')) return;
        this._container.classList.remove('choices-pending');
    }

    _showEndState() {
        if (!this._container) return;
        this._container.innerHTML = '';

        const btn = document.createElement('button');
        btn.className = 'choice-btn return-btn';
        btn.innerHTML = `<span class="choice-text">${L.get('action.return_menu') || 'Return to Main Menu'}</span>`;
        btn.addEventListener('click', () => {
            // RELOAD, not SCREEN_CHANGE: the main-menu DOM is unmounted on
            // game-view entry (layout.js showScreen). This end-state button
            // only renders after STORY_END (post-ending), i.e. always after
            // that unmount, so an in-memory screen change lands on an empty
            // black menu. Reload rebuilds it (matches pause-menu Quit).
            // foundby:Monki 0.2.8.6.
            location.reload();
        });
        this._container.appendChild(btn);
    }

    // ------------------------------------------------------------------
    // Private: Keyboard
    // ------------------------------------------------------------------

    _bindKeyboard() {
        this._keydownHandler = (e) => {
            // Only handle in game view
            if (!this._container) return;

            const _inField = e.target && (e.target.tagName === 'INPUT'
                || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable);
            const _gameView = document.getElementById('screen-game-view');
            const _inGame = _gameView && !_gameView.classList.contains('hidden');

            // Arrow-key choice navigation (accessibility — keyboard-only play,
            // foundby request 0.2.7.6.3 "space is not working, I have an injury").
            // Up/Down move a visible highlight between enabled inline choices;
            // ENTER confirms the highlighted one. Space is deliberately NOT a
            // confirm key here: players are trained to tap Space to advance
            // narration, and the first choice in NSFW beats is the consent /
            // escalation option — a reflexive Space must never auto-select it.
            // The highlight starts empty, so the first ArrowDown is a deliberate
            // opt-in (no auto-consent on menu appearance).
            if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !_inField && _inGame && !this._anyOverlayOpen()) {
                if (this._kbMove(e.key === 'ArrowDown' ? 1 : -1)) e.preventDefault();
                return;
            }
            if (e.key === 'Enter' && !_inField && _inGame && !this._anyOverlayOpen()) {
                if (this._kbConfirm()) e.preventDefault();
                return;
            }

            // Number keys 1-9, 0=9
            let num = -1;
            if (e.key >= '1' && e.key <= '9') {
                num = parseInt(e.key, 10) - 1;
            } else if (e.key === '0') {
                num = 8; // 0 maps to choice 9
            }

            if (num < 0) return;

            // Same guards as the Arrow/Enter paths above — without them a
            // number key clicks the hidden story choice behind an open
            // overlay (phone/shop/journal), or confirms a choice whose
            // framing text is still typing out (choices-pending blocks
            // pointer events but not programmatic .click()).
            if (_inField || !_inGame || this._anyOverlayOpen()) return;
            if (this._container.classList.contains('choices-pending')) return;

            // Don't trigger when location map is active (has its own hotkey handler)
            const locView = document.querySelector('.location-view-root');
            if (locView && !locView.classList.contains('hidden') && locView.children.length > 0) return;

            // Find the Nth choice button (matching displayed number, including disabled)
            const buttons = this._container.querySelectorAll('.choice-btn:not([style*="hidden"])');
            if (num < buttons.length) {
                const btn = buttons[num];
                // Skip if disabled
                if (btn.classList.contains('disabled') || btn.disabled) return;
                e.preventDefault();
                btn.click();
            }
        };
        document.addEventListener('keydown', this._keydownHandler);

        // Space-to-skip-typing lives in story-content.js _bindSkipInputs.
        // That handler also resolves pending #break: delays and guards
        // against open overlays / non-game screens. Don't duplicate here.
    }

    // True when any overlay / image modal / location map owns the keyboard.
    _anyOverlayOpen() {
        const overlays = document.querySelectorAll('.overlay');
        for (const ov of overlays) { if (!ov.classList.contains('hidden')) return true; }
        const modal = document.getElementById('image-modal');
        if (modal && !modal.classList.contains('hidden')) return true;
        const locView = document.querySelector('.location-view-root');
        if (locView && !locView.classList.contains('hidden') && locView.children.length > 0) return true;
        return false;
    }

    // Currently-visible, enabled inline choice buttons (excludes disabled,
    // spacers, the Continue button and the Return-to-menu button).
    _kbEnabledChoices() {
        if (!this._container) return [];
        return [...this._container.querySelectorAll('.choice-btn')].filter(b =>
            b.offsetParent !== null
            && b.getAttribute('aria-disabled') !== 'true'
            && b.getAttribute('aria-hidden') !== 'true'
            && !b.classList.contains('disabled')
            && !b.classList.contains('continue-btn')
            && !b.classList.contains('return-btn'));
    }

    // Move the keyboard highlight. Returns false (so the arrow keys keep their
    // native scroll behaviour) when there is no real multi-choice menu present.
    _kbMove(dir) {
        const btns = this._kbEnabledChoices();
        if (btns.length < 2) return false;
        const cur = btns.findIndex(b => b.classList.contains('choice-kb-active'));
        const next = (cur < 0)
            ? (dir > 0 ? 0 : btns.length - 1)
            : Math.min(Math.max(cur + dir, 0), btns.length - 1);
        btns.forEach(b => b.classList.remove('choice-kb-active'));
        btns[next].classList.add('choice-kb-active');
        this._scrollContainerToReveal(btns[next]);
        return true;
    }

    // Confirm the highlighted choice (Enter). No-op when nothing is highlighted
    // — that is the safety guard against reflexive auto-confirm.
    _kbConfirm() {
        if (!this._container) return false;
        // The typing-pending grid blocks mouse clicks via pointer-events:none,
        // but programmatic .click() ignores pointer-events — without this
        // check, Enter confirms a choice whose framing text hasn't rendered
        // yet (the blurred/disabled visual state).
        if (this._container.classList.contains('choices-pending')) return false;
        const active = this._container.querySelector('.choice-btn.choice-kb-active');
        if (active && active.offsetParent !== null
            && active.getAttribute('aria-disabled') !== 'true'
            && !active.classList.contains('disabled')) {
            active.click();
            return true;
        }
        return false;
    }

    // Scroll the #story container only, never propagating to ancestors.
    // scrollIntoView walks every scrollable ancestor and yanks the page
    // when #story is embedded in a scrollable host (F95 iframe, mobile
    // viewports with body overflow). Foundby:Boodstain v0.2.5.7.2.
    _scrollContainerToReveal(el) {
        if (!el) return;
        // Player opt-in: Settings > Auto Scroll. Default OFF — when disabled,
        // never auto-reveal off-screen choices. The player drives scroll
        // entirely. Counterpart guard to story-content._scrollToBottom().
        // Foundby:Kiran v0.2.5.8.1.
        try {
            if (localStorage.getItem('PnO_AutoScroll') !== '1') return;
        } catch(e) {}
        const story = document.getElementById('story');
        if (!story) return;
        const containerRect = story.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        if (elRect.bottom <= containerRect.bottom) return;
        story.scrollTo({
            top: story.scrollTop + (elRect.bottom - containerRect.bottom) + 8,
            behavior: 'smooth'
        });
    }
}

export const choicesUI = new ChoicesUI();
